import { Formik } from "formik";
import { withZodSchema } from "formik-validator-zod";
import { patchRsvp, SWRError, useRsvp } from "../api";
import { useToken } from "./TokenProvider";
import { Text, TextWithFallback } from "./i18n";
import {
  RsvpGetResponse,
  RsvpGuests,
  rsvpPatchRequest,
  RsvpPatchRequest,
} from "../../model/rsvp";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Radio, RadioGroup } from "@headlessui/react";
import { CheckCircleIcon } from "@heroicons/react/20/solid";
import { Input } from "./Input";
import { Checkbox } from "./Checkbox";
import { Button } from "./Button";
import { Transition } from "@headlessui/react";
import { ScrollToError } from "./ScrollToError";
import { Logo } from "./Logo";
import { errors } from "../../errors/errors";
import { useNotification } from "./Notification";

function isHangul(input: string): boolean {
  return !!input.match(
    /[\uac00-\ud7af]|[\u1100-\u11ff]|[\u3130-\u318f]|[\ua960-\ua97f]|[\ud7b0-\ud7ff]/g
  );
}

export const Rsvp: React.FC = () => {
  const notification = useNotification();
  const tokenContext = useToken();
  const rsvp = useRsvp(tokenContext.token);
  const navigate = useNavigate();

  const [serverError, setServerError] = useState<SWRError | null>(null);

  useEffect(() => {
    if (!tokenContext.token || rsvp.error?.code === 403) {
      navigate("/login", { replace: true });
    }
  }, [tokenContext.token, rsvp.error, navigate]);

  if (!rsvp.data || !tokenContext.token) {
    return null;
  }

  const dietaryPreferences: RsvpGuests["primary"]["dietaryPreference"][] = [
    "none",
    "vegetarian",
    "vegan",
    "other",
  ];

  const isKorean =
    isHangul(rsvp.data.guests.primary.firstName) ||
    isHangul(rsvp.data.guests.primary.lastName);

  return (
    <>
      <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-sm">
          <Logo />
          <h2 className="mt-10 text-center text-2xl/9 font-bold tracking-tight text-gray-900">
            <Text id="rsvp.respondingFor" />
            {isKorean
              ? ` ${rsvp.data?.guests.primary.lastName}${rsvp.data?.guests.primary.firstName}`
              : ` ${rsvp.data?.guests.primary.firstName} ${rsvp.data?.guests.primary.lastName}`}
          </h2>
        </div>

        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
          <Formik
            initialValues={toInitialValues(rsvp.data)}
            validate={withZodSchema(rsvpPatchRequest)}
            onSubmit={async (values) => {
              if (tokenContext.token) {
                try {
                  const resp = await patchRsvp(tokenContext.token, values);
                  rsvp.mutate(resp);
                  navigate(resp.guests.confirmed ? "/schedule" : "/");
                  notification.show();
                } catch (e) {
                  if (e instanceof SWRError) {
                    setServerError(e);
                  } else {
                    setServerError(new SWRError(1000, errors.schemaError, e));
                  }
                }
              }
            }}
          >
            {({
              values,
              errors,
              handleSubmit,
              handleChange,
              handleBlur,
              setFieldValue,
              isSubmitting,
            }) => {
              return (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <ScrollToError />
                  <div className="space-y-12">
                    <div className="border-b border-gray-900/10 pb-12">
                      <h2 className="text-base/7 font-semibold text-gray-900">
                        <Text id="rsvp.step1" />
                      </h2>
                      <div className="mt-2 grid grid-cols-1 gap-x-6">
                        <fieldset>
                          <RadioGroup
                            value={values.guests.confirmed ?? undefined}
                            onChange={(newValue) => {
                              setFieldValue(
                                "guests.confirmed",
                                newValue,
                                false
                              );
                            }}
                            className="mt-6 grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-4"
                          >
                            <Radio
                              value={true}
                              className="group relative flex cursor-pointer rounded-lg border border-gray-300 bg-white p-4 shadow-xs focus:outline-hidden data-focus:border-indigo-600 data-focus:ring-2 data-focus:ring-indigo-600"
                            >
                              <span className="flex flex-1">
                                <span className="flex flex-col">
                                  <span className="block text-sm font-medium text-gray-900">
                                    <Text id="yes" />
                                  </span>
                                </span>
                              </span>
                              <CheckCircleIcon
                                aria-hidden="true"
                                className="size-5 text-indigo-600 group-not-data-checked:invisible"
                              />
                              <span
                                aria-hidden="true"
                                className="pointer-events-none absolute -inset-px rounded-lg border-2 border-transparent group-data-checked:border-indigo-600 group-data-focus:border"
                              />
                            </Radio>
                            <Radio
                              value={false}
                              className="group relative flex cursor-pointer rounded-lg border border-gray-300 bg-white p-4 shadow-xs focus:outline-hidden data-focus:border-indigo-600 data-focus:ring-2 data-focus:ring-indigo-600"
                            >
                              <span className="flex flex-1">
                                <span className="flex flex-col">
                                  <span className="block text-sm font-medium text-gray-900">
                                    <Text id="no" />
                                  </span>
                                </span>
                              </span>
                              <CheckCircleIcon
                                aria-hidden="false"
                                className="size-5 text-indigo-600 group-not-data-checked:invisible"
                              />
                              <span
                                aria-hidden="false"
                                className="pointer-events-none absolute -inset-px rounded-lg border-2 border-transparent group-data-checked:border-indigo-600 group-data-focus:border"
                              />
                            </Radio>
                          </RadioGroup>
                        </fieldset>
                        {rsvp.data?.guests.personalizedInfo && (
                          <p className="mt-2">
                            * {rsvp.data?.guests.personalizedInfo}
                          </p>
                        )}
                      </div>
                    </div>
                    <Transition show={values.guests.confirmed}>
                      <div className="space-y-12 transition duration-300 ease-in data-[closed]:opacity-0">
                        <div className="border-b border-gray-900/10 pb-12">
                          <h2 className="text-base/7 font-semibold text-gray-900">
                            <Text id="rsvp.step2" />
                          </h2>
                          <div className="mt-2 grid grid-cols-1 gap-x-6 gap-y-8">
                            <fieldset className="relative -space-y-px rounded-md bg-white">
                              {dietaryPreferences.map((dietaryPreference) => (
                                <label
                                  key={dietaryPreference}
                                  className="group flex cursor-pointer flex-col border border-gray-200 p-4 first:rounded-tl-md first:rounded-tr-md last:rounded-br-md last:rounded-bl-md focus:outline-hidden has-checked:relative has-checked:border-indigo-200 has-checked:bg-indigo-50 md:grid md:grid-cols-1 md:pr-6 md:pl-4"
                                >
                                  <span className="flex items-center gap-3 text-sm">
                                    <input
                                      defaultValue={dietaryPreference}
                                      defaultChecked={
                                        dietaryPreference ===
                                        values.guests.primary.dietaryPreference
                                      }
                                      name={`guests.primary.dietaryPreference`}
                                      onChange={(e) =>
                                        setFieldValue(
                                          "guests.primary.dietaryPreference",
                                          e.currentTarget.value,
                                          true
                                        )
                                      }
                                      type="radio"
                                      className="relative size-4 appearance-none rounded-full border border-gray-300 bg-white before:absolute before:inset-1 before:rounded-full before:bg-white not-checked:before:hidden checked:border-indigo-600 checked:bg-indigo-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 forced-colors:appearance-auto forced-colors:before:hidden"
                                    />
                                    <span className="font-medium text-gray-900 group-has-checked:text-indigo-900">
                                      <TextWithFallback
                                        id={`rsvp.dietaryPreference.${dietaryPreference}`}
                                        fallbackId="rsvp.dietaryPreference.other"
                                      />
                                    </span>
                                  </span>
                                </label>
                              ))}
                            </fieldset>
                            <div>
                              <Input
                                name="guests.primary.dietaryExtra"
                                type="text"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.guests.primary.dietaryExtra ?? ""}
                                label={<Text id="rsvp.dietaryExtra" />}
                                hint={<Text id="common.optional" />}
                                error={
                                  errors.guests?.primary?.dietaryExtra ? (
                                    <TextWithFallback
                                      id={errors.guests?.primary?.dietaryExtra}
                                      fallbackId="errors.validation.generic"
                                    />
                                  ) : null
                                }
                                textarea
                              />
                            </div>
                          </div>
                        </div>
                        {isKorean && (
                          <div className="border-b border-gray-900/10 pb-12">
                            <h2 className="text-base/7 font-semibold text-gray-900">
                              <Text id="rsvp.step3" />
                            </h2>
                            <p className="mt-1 text-sm/6 text-gray-600">
                              <Text id="rsvp.step3.description" />
                            </p>
                            <div className="mt-2 grid grid-cols-1 gap-x-6 gap-y-8">
                              <div>
                                <Input
                                  name="guests.primary.nametag"
                                  type="text"
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  value={values.guests.primary.nametag ?? ""}
                                  label={<Text id="rsvp.nametag" />}
                                  hint={<Text id="common.optional" />}
                                  error={
                                    errors.guests?.primary?.nametag ? (
                                      <TextWithFallback
                                        id={errors.guests?.primary?.nametag}
                                        fallbackId="errors.validation.generic"
                                      />
                                    ) : null
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        )}
                        <div className="border-b border-gray-900/10 pb-12">
                          <h2 className="text-base/7 font-semibold text-gray-900">
                            <Text id="rsvp.step4" />
                          </h2>
                          <div className="mt-2 mb-2">
                            <Checkbox
                              name="guests.hasPartner"
                              onChange={(e) =>
                                setFieldValue(
                                  "guests.partner",
                                  e.currentTarget.checked
                                    ? ({
                                        dietaryPreference: "none",
                                        dietaryExtra: null,
                                        firstName: "",
                                        lastName: "",
                                        nametag: "",
                                        ...rsvp.data?.guests.partner,
                                        ...values.guests.partner,
                                      } as InitialValues["guests"]["partner"])
                                    : null,
                                  false
                                )
                              }
                              onBlur={handleBlur}
                              checked={!!values.guests.partner}
                              label={<Text id="rsvp.hasParner" />}
                            />
                          </div>
                          <Transition show={!!values.guests.partner}>
                            <div className="mt-2 grid grid-cols-1 gap-x-6 gap-y-4 transition duration-300 ease-in data-[closed]:opacity-0">
                              <div>
                                <Input
                                  name="guests.partner.firstName"
                                  type="text"
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  value={values.guests.partner?.firstName ?? ""}
                                  label={<Text id="rsvp.firstName" />}
                                  error={
                                    // @ts-expect-error bad types
                                    errors.guests?.partner?.firstName ? (
                                      <TextWithFallback
                                        // @ts-expect-error bad types
                                        id={errors.guests?.partner?.firstName}
                                        fallbackId="errors.validation.generic"
                                      />
                                    ) : null
                                  }
                                />
                              </div>
                              <div>
                                <Input
                                  name="guests.partner.lastName"
                                  type="text"
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  value={values.guests.partner?.lastName ?? ""}
                                  label={<Text id="rsvp.lastName" />}
                                  error={
                                    // @ts-expect-error bad types
                                    errors.guests?.partner?.lastName ? (
                                      <TextWithFallback
                                        // @ts-expect-error bad types
                                        id={errors.guests?.partner?.lastName}
                                        fallbackId="errors.validation.generic"
                                      />
                                    ) : null
                                  }
                                />
                              </div>
                              <Transition
                                show={
                                  isHangul(
                                    values.guests.partner?.firstName ?? ""
                                  ) ||
                                  isHangul(
                                    values.guests.partner?.lastName ?? ""
                                  )
                                }
                              >
                                <div className="transition duration-300 ease-in data-[closed]:opacity-0">
                                  <Input
                                    name="guests.partner.nametag"
                                    type="text"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values.guests.partner?.nametag ?? ""}
                                    label={<Text id="rsvp.nametag" />}
                                    hint={<Text id="common.optional" />}
                                    error={
                                      // @ts-expect-error bad types
                                      errors.guests?.partner?.nametag ? (
                                        <TextWithFallback
                                          // @ts-expect-error bad types
                                          id={errors.guests?.partner?.nametag}
                                          fallbackId="errors.validation.generic"
                                        />
                                      ) : null
                                    }
                                  />
                                </div>
                              </Transition>
                            </div>
                          </Transition>
                        </div>
                        <Transition show={!!values.guests.partner}>
                          <div className="border-b border-gray-900/10 pb-12 transition duration-300 ease-in data-[closed]:opacity-0">
                            <h2 className="text-base/7 font-semibold text-gray-900">
                              <Text id="rsvp.step5" />
                            </h2>
                            <div className="mt-2 grid grid-cols-1 gap-x-6 gap-y-8">
                              <fieldset className="relative -space-y-px rounded-md bg-white">
                                {dietaryPreferences.map((dietaryPreference) => (
                                  <label
                                    key={dietaryPreference}
                                    className="group flex cursor-pointer flex-col border border-gray-200 p-4 first:rounded-tl-md first:rounded-tr-md last:rounded-br-md last:rounded-bl-md focus:outline-hidden has-checked:relative has-checked:border-indigo-200 has-checked:bg-indigo-50 md:grid md:grid-cols-1 md:pr-6 md:pl-4"
                                  >
                                    <span className="flex items-center gap-3 text-sm">
                                      <input
                                        defaultValue={dietaryPreference}
                                        defaultChecked={
                                          dietaryPreference ===
                                          values.guests.partner
                                            ?.dietaryPreference
                                        }
                                        name={`guests.partner.dietaryPreference`}
                                        onChange={(e) =>
                                          setFieldValue(
                                            "guests.partner.dietaryPreference",
                                            e.currentTarget.value,
                                            true
                                          )
                                        }
                                        type="radio"
                                        className="relative size-4 appearance-none rounded-full border border-gray-300 bg-white before:absolute before:inset-1 before:rounded-full before:bg-white not-checked:before:hidden checked:border-indigo-600 checked:bg-indigo-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 forced-colors:appearance-auto forced-colors:before:hidden"
                                      />
                                      <span className="font-medium text-gray-900 group-has-checked:text-indigo-900">
                                        <TextWithFallback
                                          id={`rsvp.dietaryPreference.${dietaryPreference}`}
                                          fallbackId="rsvp.dietaryPreference.other"
                                        />
                                      </span>
                                    </span>
                                  </label>
                                ))}
                              </fieldset>
                              <div>
                                <Input
                                  name="guests.partner.dietaryExtra"
                                  type="text"
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  value={
                                    values.guests.partner?.dietaryExtra ?? ""
                                  }
                                  label={<Text id="rsvp.dietaryExtra" />}
                                  hint={<Text id="common.optional" />}
                                  error={
                                    // @ts-expect-error bad types
                                    errors.guests?.partner?.dietaryExtra ? (
                                      <TextWithFallback
                                        id={
                                          // @ts-expect-error bad types
                                          errors.guests?.partner?.dietaryExtra
                                        }
                                        fallbackId="errors.validation.generic"
                                      />
                                    ) : null
                                  }
                                  textarea
                                />
                              </div>
                            </div>
                          </div>
                        </Transition>
                        {!!rsvp.data?.guests.roomAssigned && (
                          <div className="border-b border-gray-900/10 pb-12">
                            <h2 className="text-base/7 font-semibold text-gray-900">
                              <Text id="rsvp.step6" />
                            </h2>
                            <p className="mt-1 text-sm/6 text-gray-600">
                              <Text id="rsvp.step6.description" />
                            </p>
                            <div className="mt-2 grid grid-cols-1 gap-x-6 gap-y-8">
                              <fieldset>
                                <RadioGroup
                                  value={
                                    values.guests.roomConfirmed ?? undefined
                                  }
                                  onChange={(newValue) => {
                                    setFieldValue(
                                      "guests.roomConfirmed",
                                      newValue,
                                      false
                                    );
                                  }}
                                  className="mt-6 grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-4"
                                >
                                  <Radio
                                    value={true}
                                    className="group relative flex cursor-pointer rounded-lg border border-gray-300 bg-white p-4 shadow-xs focus:outline-hidden data-focus:border-indigo-600 data-focus:ring-2 data-focus:ring-indigo-600"
                                  >
                                    <span className="flex flex-1">
                                      <span className="flex flex-col">
                                        <span className="block text-sm font-medium text-gray-900">
                                          <Text id="accept" />
                                        </span>
                                      </span>
                                    </span>
                                    <CheckCircleIcon
                                      aria-hidden="true"
                                      className="size-5 text-indigo-600 group-not-data-checked:invisible"
                                    />
                                    <span
                                      aria-hidden="true"
                                      className="pointer-events-none absolute -inset-px rounded-lg border-2 border-transparent group-data-checked:border-indigo-600 group-data-focus:border"
                                    />
                                  </Radio>
                                  <Radio
                                    value={false}
                                    className="group relative flex cursor-pointer rounded-lg border border-gray-300 bg-white p-4 shadow-xs focus:outline-hidden data-focus:border-indigo-600 data-focus:ring-2 data-focus:ring-indigo-600"
                                  >
                                    <span className="flex flex-1">
                                      <span className="flex flex-col">
                                        <span className="block text-sm font-medium text-gray-900">
                                          <Text id="decline" />
                                        </span>
                                      </span>
                                    </span>
                                    <CheckCircleIcon
                                      aria-hidden="false"
                                      className="size-5 text-indigo-600 group-not-data-checked:invisible"
                                    />
                                    <span
                                      aria-hidden="false"
                                      className="pointer-events-none absolute -inset-px rounded-lg border-2 border-transparent group-data-checked:border-indigo-600 group-data-focus:border"
                                    />
                                  </Radio>
                                </RadioGroup>
                              </fieldset>
                            </div>
                          </div>
                        )}
                        <div className="border-b border-gray-900/10 pb-12">
                          <h2 className="text-base/7 font-semibold text-gray-900">
                            <Text id="rsvp.step7" />
                          </h2>
                          <p className="mt-1 text-sm/6 text-gray-600">
                            <Text id="rsvp.step7.description" />
                          </p>
                          <div className="mt-2 grid grid-cols-1 gap-x-6 gap-y-8">
                            <div>
                              <Input
                                name="guests.email"
                                type="email"
                                placeholder={"you@email.com"}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.guests.email ?? ""}
                                label={<Text id="rsvp.email" />}
                                hint={<Text id="common.optional" />}
                                error={
                                  errors.guests?.email ? (
                                    <TextWithFallback
                                      id={errors.guests?.email}
                                      fallbackId="errors.validation.generic"
                                    />
                                  ) : null
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </Transition>
                    <div>
                      {serverError && (
                        <p className="text-red-500 mb-2">
                          <TextWithFallback
                            id={serverError.message}
                            fallbackId="errors.1000"
                          />
                        </p>
                      )}
                      <Button type="submit" isLoading={isSubmitting}>
                        <Text id="rsvp.saveResponse" />
                      </Button>
                    </div>
                  </div>
                </form>
              );
            }}
          </Formik>
        </div>
      </div>
    </>
  );
};

type InitialValues = {
  languagePreference: RsvpPatchRequest["languagePreference"];
  guests: NonNullable<RsvpPatchRequest["guests"]>;
};

function toInitialValues(rsvp: RsvpGetResponse): InitialValues {
  const p: InitialValues = {
    languagePreference: rsvp.languagePreference,
    guests: {
      primary: {
        nametag: rsvp.guests.primary.nametag,
        dietaryPreference: rsvp.guests.primary.dietaryPreference,
        dietaryExtra: rsvp.guests.primary.dietaryExtra,
      },
      partner: rsvp.guests.partner === null ? null : { ...rsvp.guests.partner },
      confirmed: rsvp.guests.confirmed === null ? true : rsvp.guests.confirmed,
      email: rsvp.guests.email,
      roomConfirmed: rsvp.guests.roomAssigned
        ? rsvp.guests.roomConfirmed === null
          ? true
          : rsvp.guests.roomConfirmed
        : null,
    },
  };
  return p;
}
